import React, { useEffect } from "react";
import { Question } from "../../types";
import { SjtAnswerList } from "./SjtAnswerList";
import {
  getDefaultSjtOrderIds,
  getSjtLetterByAnswerId,
} from "./sjtUtils";
import { shouldIgnoreQuestionNavigationShortcut } from "../../utils/shouldIgnoreQuestionNavigationShortcut";

type SjtPracticeBlockProps = {
  question: Question;
  practiceQuestionAnswered: boolean;
  selection: number[];
  delimeters: any[];
  onSelectionChange: (newSelection: number[]) => void;
  onSubmit: (orderedIds: number[]) => void;
  hideBottomSubmitButton?: boolean;
  hideBottomClearButton?: boolean;
};

export const SjtPracticeBlock: React.FC<SjtPracticeBlockProps> = ({
  question,
  practiceQuestionAnswered,
  selection,
  delimeters,
  onSelectionChange,
  onSubmit,
  hideBottomSubmitButton,
  hideBottomClearButton,
}) => {
  const initialLetterByAnswerId = getSjtLetterByAnswerId(question);
  const orderedSelection =
    selection.length === question.answerOptions.length
      ? selection
      : getDefaultSjtOrderIds(question);

  // Ensure parent always holds a full ranking so submit / review stay consistent
  useEffect(() => {
    if (
      !practiceQuestionAnswered &&
      selection.length !== question.answerOptions.length
    ) {
      onSelectionChange(getDefaultSjtOrderIds(question));
    }
    // Only re-sync when the question or answered state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id, practiceQuestionAnswered]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (practiceQuestionAnswered) return;
      if (shouldIgnoreQuestionNavigationShortcut(e)) return;
      if (e.key === "Enter") {
        onSubmit(orderedSelection);
        e.preventDefault();
        return;
      }
      if (e.key === "Escape") {
        onSelectionChange(getDefaultSjtOrderIds(question));
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    practiceQuestionAnswered,
    question,
    orderedSelection,
    onSelectionChange,
    onSubmit,
  ]);

  return (
    <div style={{ width: "100%" }}>
      <SjtAnswerList
        question={question}
        practiceQuestionAnswered={practiceQuestionAnswered}
        selection={orderedSelection}
        initialLetterByAnswerId={initialLetterByAnswerId}
        delimeters={delimeters}
        onSelectionChange={onSelectionChange}
        onClear={() => {
          if (practiceQuestionAnswered) return;
          onSelectionChange(getDefaultSjtOrderIds(question));
        }}
        onSubmit={() => onSubmit(orderedSelection)}
        hideSubmitButton={hideBottomSubmitButton}
        hideClearButton={hideBottomClearButton}
      />
    </div>
  );
};
